// src/components/wallet/LpPerformance.js
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Chart } from 'primereact/chart';
import Web3 from 'web3';
import lpSeederAbi from '../../contracts/lpSeederAbi';
import { useWeb3React } from '@web3-react/core';
import { syncPositionForPair } from './multiPairSync';
import { formatPercentage } from '../../utils';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded'; 

const LPSEEDER = '0x753ebB494A4f74A43B76614AF0F8a8719cE9fECE';
const web3 = new Web3(
  new Web3.providers.HttpProvider(
    'https://binance.llamarpc.com/sk_llama_e2e9915b5438c5313ae3d634858d85cb'
  )
);

function LpPerformance({ lpToken, account, getTokenLogoUrl }) {
    const { library } = useWeb3React();  
  const [status, setStatus]     = useState('loading');
  const [positions, setPositions] = useState([]);
  const [seedEvents, setSeedEvents] = useState([]);
    const [syncing, setSyncing]   = useState(false);
    const [livePoint, setLivePoint] = useState(null); 
   const chartRef = useRef(null);

    /* ─── Fetch once per pair ───────────────────────────── */
  useEffect(() => {
    if (!account || !lpToken?.pairAddress) return;

    (async () => {
      setStatus('loading');

      const c = new web3.eth.Contract(lpSeederAbi, LPSEEDER);

      try {
        // 1️⃣ length of UserPosition history – exists even after manual sync
        const len = Number(
          await c.methods
            .getUserPositionHistoryLength(account, lpToken.pairAddress)
            .call()
        );

        if (len === 0) {
          // absolutely nothing on-chain yet → show “no hist”
          setStatus('nohist');
          return;
        }

        // 2️⃣ pull positions + (optional) seed in/out
        const [posRaw, ins, outs] = await Promise.all([
          c.methods
            .getUserPositionHistoryByIndexRange(account, lpToken.pairAddress, 0, len)
            .call(),
          c.methods
            .getSeedingInHistoryByIndexRange(account, lpToken.pairAddress, 0, 1000)
            .call(),
          c.methods
            .getSeedingOutHistoryByIndexRange(account, lpToken.pairAddress, 0, 1000)
            .call()
        ]);

        /* -------- positions --------------------------------------------------- */
        setPositions(
          posRaw
            .filter(p => Number(p.timestamp) > 0)
            .map(p => ({
              ts: Number(p.timestamp) * 1000,
              s0: Number(p.token0Share),
              s1: Number(p.token1Share)
            }))
        );

        /* -------- seed events (may be empty) ---------------------------------- */
        const events = [
          ...ins.map(s => ({
            ts: Number(s.timestamp) * 1000,
            t0: Number(s.token0Amount),
            t1: Number(s.token1amount),
            dir: 'in'
          })),
          ...outs.map(s => ({
            ts: Number(s.timestamp) * 1000,
            t0: Number(s.token0Amount),
            t1: Number(s.token1amount),
            dir: 'out'
          }))
        ]
          .filter(e => e.ts > 0)
          .sort((a, b) => a.ts - b.ts);

        setSeedEvents(events);
        setStatus('ready');
      } catch (err) {
        console.error('LP perf error', err);
        setStatus('nohist');
      }
    })();
  }, [account, lpToken.pairAddress]);

  /* ─── Fetch live point every 60s ────────────────────── */
  useEffect(() => {
  if (status !== 'ready') return;                 // only when we already have history
  if (!account || !lpToken?.pairAddress) return;

  const w3  = library ? new Web3(library.provider) : web3;
  const ctr = new w3.eth.Contract(lpSeederAbi, LPSEEDER);

  const fetchLive = async () => {
    try {
      const p  = await ctr.methods
                  .getCurrentUserShare(lpToken.pairAddress, account)
                  .call();
      setLivePoint({
        ts: Date.now(),                 // current wall-clock ms
        s0: Number(p.token0Share),
        s1: Number(p.token1Share)
      });
    } catch (e) {
      console.error('live-point fetch error', e);
    }
  };

  fetchLive();                           // immediate first hit
  const id = setInterval(fetchLive, 60_000);
  return () => clearInterval(id);        // cleanup on unmount / pair change
}, [status, account, lpToken.pairAddress, library]);

  /* ─── Build chart config only when data changes ─────── */
  const cfg = useMemo(() => {
  if (status !== 'ready') return null;

  /* ------------------------------------------------------------------
     Is lpToken.token the pair's token0 or token1?  ------------------- */
  const primaryIs0 =
    lpToken.token0Address &&
    lpToken.token0Address.toLowerCase() === lpToken.token.toLowerCase();

  /* decimals --------------------------------------------------------- */
  const decPrimary  = lpToken.tokenDecimals || 18;
  const decBacking  = lpToken.backingTokenDecimals || 18;

  /* time-series ------------------------------------------------------ */
  const allPos    = livePoint ? [...positions, livePoint] : positions;
  //const fmt         = d => new Date(d).toISOString().slice(5,16).replace('T',' ');   // “MM-DD HH:MM”
  const fmt = ts =>
  new Date(ts).toLocaleString(undefined, {
    month : '2-digit',
    day   : '2-digit',
    hour  : '2-digit',
    minute: '2-digit',
    hour12: true   // 24-hour clock; remove if you prefer 12-hour
  });
  const labels      = allPos.map(p => fmt(p.ts));

  const rawPrimary  = allPos.map(p =>
   (primaryIs0 ? p.s0 : p.s1) / 10 ** decPrimary
  );
  const rawBacking  = allPos.map(p =>
   (primaryIs0 ? p.s1 : p.s0) / 10 ** decBacking
 );

  /* seed dots (±1 track) -------------------------------------------- */
  const scatterPrimary = seedEvents.map(e => (e.dir === 'in' ? 1 : -1));
  const scatterBacking = seedEvents.map(e => (e.dir === 'in' ? 1 : -1));
  const scatterLab     = seedEvents.map(e => fmt(e.ts));

  /* helper ----------------------------------------------------------- */
  const pad = arr => {
    const mn = Math.min(...arr),
      mx = Math.max(...arr),
      rng = mx - mn || 1;
    return { min: mn - rng * 0.05, max: mx + rng * 0.05 };
  };

  /* % metrics -------------------------------------------------------- */
  const pctPrimStart  =
    rawPrimary.length > 1 ? ((rawPrimary.at(-1) - rawPrimary[0]) / rawPrimary[0]) : 0;
  const pctBackStart  =
    rawBacking.length > 1 ? ((rawBacking.at(-1) - rawBacking[0]) / rawBacking[0]) : 0;
  const pctPrimRecent =
  rawPrimary.length > 1
    ? ((rawPrimary.at(-1) - rawPrimary.at(-2)) / rawPrimary.at(-2))
    : 0;

const pctBackRecent =
  rawBacking.length > 1
    ? ((rawBacking.at(-1) - rawBacking.at(-2)) / rawBacking.at(-2))
    : 0;

    const pctTotalStart  = (pctPrimStart  + pctBackStart)  / 2;
    const pctTotalRecent = (pctPrimRecent + pctBackRecent) / 2;

  /* datasets --------------------------------------------------------- */
  return {
    pct0_sinceStart: pctPrimStart,
    pct1_sinceStart: pctBackStart,
    pct0_recent:     pctPrimRecent,
    pct1_recent:     pctBackRecent,
    pct_total_sinceStart: pctTotalStart,
    pct_total_recent: pctTotalRecent,
    data: {
  labels,
  datasets: [
    // Primary-token share
    {
      label: `${lpToken.tokenSymbol} Share`,
      data: rawPrimary,
      borderColor: '#46a2ff',
      tension: 0.3,
      borderWidth: 2,
      pointRadius: 3,
      yAxisID: 'y0',
    },
    // Backing-token share
    {
      label: `${lpToken.backingTokenSymbol} Share`,
      data: rawBacking,
      borderColor: '#f779a1',
      tension: 0.3,
      borderWidth: 2,
      pointRadius: 3,
      yAxisID: 'y1'
    },

    /* ── add seed dots only if they exist ───────────────────────── */
    ...(
      seedEvents.length === 0
        ? []
        : [
            {
              type: 'scatter',
              label: `Seed in/out ${lpToken.tokenSymbol}`,
              data: scatterPrimary.map((v, i) => ({ x: scatterLab[i], y: v })),
              borderColor: '#46a2ff40',
              backgroundColor: '#46a2ff40',
              pointStyle: 'circle',
              pointRadius: 8,
              showLine: false,
              yAxisID: 'seed'
            },
            {
              type: 'scatter',
              label: `Seed in/out ${lpToken.backingTokenSymbol}`,
              data: scatterBacking.map((v, i) => ({ x: scatterLab[i], y: v })),
              borderColor: '#f779a140',
              backgroundColor: '#f779a140',
              pointStyle: 'circle',
              pointRadius: 8,
              showLine: false,
              yAxisID: 'seed'
            }
          ]
    )
  ]
},

/* scales block */
scales: {
  y0: {
    position: 'left',
    ...pad(rawPrimary),
    ticks: { color: '#46a2ff', font: { size: 9 } },
    grid:  { color: 'rgba(255,255,255,0.05)' }
  },
  y1: {
    position: 'right',
    ...pad(rawBacking),
    ticks: { color: '#f779a1', font: { size: 9 } },
    grid:  { drawOnChartArea: false }
  },
  ...(seedEvents.length === 0
      ? {}                              // if no seed events → no extra axis
      : {
          seed: {
            position: 'right',
            display: false,
            min: -1.3,
            max: 1.3
          }
        })
}

  };
}, [status, positions, seedEvents, livePoint, lpToken]);




  /* ─── Tooltip formatter uses parsed y directly (no extra ÷10ⁿ) */
  const tooltipLabel = useCallback(
    //ctx => `${ctx.dataset.label}: ${Number(ctx.parsed.y).toLocaleString(undefined,{maximumFractionDigits:4})}`,
    ctx => {
      const label = `${ctx.dataset.label}: ${Number(ctx.parsed.y)
                      .toLocaleString(undefined,{maximumFractionDigits:4})}`;
      if (ctx.datasetIndex < 2) {                                           // primary/backing only
        const pct = ctx.datasetIndex === 0 ? cfg?.pct_total_sinceStart : null;
      }
      return label;
    },
    []
  );

  const Delta = useCallback(({ pct }) => (
    <span style={{display:'flex',alignItems:'center',gap:2,fontSize:11,
      color:pct>=0?'limegreen':'crimson'}}>
      {pct>=0 ? <ArrowUpwardIcon style={{fontSize:12}}/> :
                <ArrowDownwardIcon style={{fontSize:12}}/>}
      {formatPercentage(Math.abs(pct))}
    </span>
  ), []);

  const LabelIcon = ({children}) => (
    <span style={{display:'flex',alignItems:'center',gap:4}}>
      {children}
    </span>
  );

  /* ─── early states ─────────────────────────────────── */
  if (status==='loading') return <p style={{fontSize:11,color:'grey'}}>Loading LP performance…</p>;
  if (status==='nosync' || status==='nohist')  return <div>
    <p style={{fontSize:11,color:'grey'}}>Not synced yet. Tap “Sync All LP”.</p>

    </div>
    ;

  /* ─── render ───────────────────────────────────────── */
  return (
    <div style={{marginTop:10}}>
      
        <div style={{
          display: 'flex', flexDirection: 'column', background: 'rgba(0,0,0,0.65)',
          padding: '4px 8px', borderRadius: '8px 8px 0 0', fontSize: 12, color: '#fff'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <img src={getTokenLogoUrl({ address: lpToken.token, symbol: lpToken.tokenSymbol })}
          alt="" style={{ width: 16, height: 16, borderRadius: '50%' }} />
              {lpToken.tokenSymbol}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <span>Since Start <Delta pct={cfg.pct0_sinceStart} /></span>
              <span>Recent <Delta pct={cfg.pct0_recent} /></span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <img src={getTokenLogoUrl({ address: lpToken.backingToken, symbol: lpToken.backingTokenSymbol })}
          alt="" style={{ width: 16, height: 16, borderRadius: '50%' }} />
              {lpToken.backingTokenSymbol}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <span>Since Start <Delta pct={cfg.pct1_sinceStart} /></span>
              <span>Recent <Delta pct={cfg.pct1_recent} /></span>
            </div>
          </div>
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:4 }}>
          <LabelIcon><StorageRoundedIcon style={{fontSize:16}}/> LP Total</LabelIcon>
          <div style={{display:'flex',gap:8}}>
            <span>Since Start <Delta pct={cfg.pct_total_sinceStart}/></span>
            <span>Recent <Delta pct={cfg.pct_total_recent}/></span>
          </div>
        </div>
        </div>
      {/* chart */}
      <div style={{background:'#0e0e0e',borderRadius:'0 0 8px 8px',padding:4}}>
        <Chart
          ref={chartRef}
          type="line"
          data={cfg.data}
          options={{
            responsive:true, maintainAspectRatio:false,
            plugins:{
              legend:{position:'bottom',labels:{color:'#fff',font:{size:10}}},
              tooltip:{callbacks:{label:tooltipLabel}}
            },
            scales:{
              x:{ticks:{color:'#aaa',font:{size:9}},grid:{color:'rgba(255,255,255,0.05)'}},
              y0:cfg.scales.y0, y1:cfg.scales.y1
            }
          }}
          style={{height:220}}
        />
      </div>
    </div>
  );
}

export default React.memo(
  LpPerformance,
  (a,b)=>a.account===b.account && a.lpToken.pairAddress===b.lpToken.pairAddress
);
